import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Search } from "lucide-react";

import { ApiError } from "@/lib/api";
import { fetchWikiOAuthStatus, searchWiki, startWikiOAuth } from "@/lib/wiki";
import { cn } from "@/lib/utils";

export function WikiPage() {
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  const statusQuery = useQuery({
    queryKey: ["wiki", "oauth-status"],
    queryFn: fetchWikiOAuthStatus,
    retry: false,
  });
  const connected = Boolean(statusQuery.data?.connected);
  const configured = statusQuery.data?.configured ?? true;
  const searchQuery = useQuery({
    queryKey: ["wiki", "search", trimmedQuery],
    queryFn: () => searchWiki(trimmedQuery, 12),
    enabled: connected && trimmedQuery.length >= 2,
    retry: false,
    staleTime: 20_000,
  });

  const statusMessage = useMemo(() => {
    if (statusQuery.isLoading) return "Checking your Outline connection...";
    if (!configured) return "Outline OAuth is not configured on this server.";
    if (!connected) return "Connect Outline to search the wiki with your own document permissions.";
    return "Search uses your Outline identity, so results match the pages you can access.";
  }, [connected, configured, statusQuery.isLoading]);

  const beginOutlineSignIn = () => {
    void startWikiOAuth("/wiki")
      .then((response) => {
        window.location.assign(response.authorizeUrl);
      });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Wiki</h1>
          <p className="mt-2 text-sm text-slate-500">{statusMessage}</p>
        </div>
        <a
          href="https://app.getoutline.com"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        >
          Go to Outline
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      </header>

      {!connected ? (
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-base font-semibold text-slate-900">Connect Outline</h2>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            The hub needs an Outline authorization for your user before it can search private wiki pages.
          </p>
          <button
            type="button"
            disabled={!configured || statusQuery.isLoading}
            onClick={beginOutlineSignIn}
            className={cn(
              "mt-4 inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2",
              configured && !statusQuery.isLoading
                ? "bg-brand-600 text-white hover:bg-brand-700"
                : "cursor-not-allowed bg-slate-200 text-slate-500",
            )}
          >
            Connect Outline
          </button>
        </section>
      ) : (
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Search the wiki</span>
            <div className="relative mt-2">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search pages, procedures, policies..."
                autoComplete="off"
                className="w-full rounded-md border border-slate-200 py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
          </label>
        </section>
      )}

      {connected && trimmedQuery.length < 2 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
          Type at least 2 characters to search Outline.
        </p>
      ) : null}

      {searchQuery.isFetching ? (
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Searching Outline...
        </div>
      ) : null}

      {searchQuery.error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          {searchQuery.error instanceof ApiError && searchQuery.error.status === 409
            ? "Outline sign-in is required before searching."
            : searchQuery.error.message}
        </div>
      ) : null}

      {searchQuery.data && searchQuery.data.results.length === 0 && trimmedQuery.length >= 2 && !searchQuery.isFetching ? (
        <p className="rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
          No wiki pages matched your search.
        </p>
      ) : null}

      {searchQuery.data && searchQuery.data.results.length > 0 ? (
        <ul className="space-y-3">
          {searchQuery.data.results.map((result) => (
            <li key={result.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-slate-900">{result.title}</h2>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                    {result.collectionName ? <span>{result.collectionName}</span> : null}
                    {result.updatedAt ? <span>Updated {new Date(result.updatedAt).toLocaleDateString()}</span> : null}
                  </div>
                  {result.context ? (
                    <p className="mt-2 text-sm leading-6 text-slate-600">{result.context}</p>
                  ) : null}
                </div>
                <Link
                  to={`/wiki/docs/${encodeURIComponent(result.id)}`}
                  className="inline-flex shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                >
                  View in hub
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
        <h2 className="font-semibold text-slate-900">How this works</h2>
        <p className="mt-2 text-sm text-slate-500">
          The hub searches Outline with your connected Outline account. Results open in the hub first, with a link to
          open the source page in Outline for editing, comments, and history.
        </p>
      </section>
    </div>
  );
}
